import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Container,
  Group,
  Loader,
  Modal,
  Notification,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Badge,
  Center,
  Radio,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconCheck,
  IconDoorExit,
  IconEdit,
  IconRefresh,
  IconUserPlus,
  IconUsers,
  IconX,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axiosConfig';

const GuestTracking = () => {
  const navigate = useNavigate();

  const [activeGuests, setActiveGuests] = useState([]);
  const [loading, setLoading] = useState(false);

  // Radio button seçimi için state ('name' veya 'vehicle')
  const [entryMode, setEntryMode] = useState('name');

  const [formData, setFormData] = useState({
    visitor_name: '',
    host_name: '',
    reason: '',
  });

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingGuest, setEditingGuest] = useState({
    id: null,
    visitor_name: '',
    host_name: '',
    reason: '',
  });

  const [alertInfo, setAlertInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const [confirmInfo, setConfirmInfo] = useState({
    show: false,
    id: null,
  });

  const [submittingEntry, setSubmittingEntry] = useState(false);
  const [submittingEdit, setSubmittingEdit] = useState(false);
  const [submittingExit, setSubmittingExit] = useState(false);

  useEffect(() => {
    fetchGuests();
  }, []);

  const showCustomAlert = (type, message) => {
    setAlertInfo({ show: true, type, message });

    if (type === 'success') {
      setTimeout(() => {
        setAlertInfo({ show: false, type: '', message: '' });
      }, 2000);
    }
  };

  const fetchGuests = async () => {
    setLoading(true);
    try {
      // Endpoint güncellendi
      const res = await api.get('/istanbul-office/guests/active');
      setActiveGuests(res.data || []);
    } catch (err) {
      console.error('Aktif misafirler alınamadı:', err);
      showCustomAlert(
        'error',
        err.response?.data?.detail || 'Aktif misafirler alınamadı.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEntry = async (e) => {
    e.preventDefault();

    if (!formData.visitor_name.trim() || !formData.host_name.trim()) {
      showCustomAlert('error', 'Lütfen gerekli alanları doldurun.');
      return;
    }

    setSubmittingEntry(true);
    try {
      // Endpoint güncellendi
      await api.post('/istanbul-office/guests/entry', {
        visitor_name: formData.visitor_name.trim(),
        host_name: formData.host_name.trim(),
        reason: formData.reason.trim(),
      });

      showCustomAlert('success', 'Misafir girişi kaydedildi.');
      setFormData({ visitor_name: '', host_name: '', reason: '' });
      fetchGuests();
    } catch (error) {
      console.error('Misafir giriş hatası:', error);
      showCustomAlert(
        'error',
        error.response?.data?.detail || 'Giriş işlemi başarısız.'
      );
    } finally {
      setSubmittingEntry(false);
    }
  };

  const openEditModal = (guest) => {
    setEditingGuest({
      id: guest.id,
      visitor_name: guest.visitor_name || '',
      host_name: guest.host_name || '',
      reason: guest.reason || '',
    });
    setIsEditModalOpen(true);
  };

  const handleUpdateGuest = async (e) => {
    e.preventDefault();

    if (!editingGuest.visitor_name.trim() || !editingGuest.host_name.trim()) {
      showCustomAlert('error', 'Misafir adı ve ev sahibi alanları zorunludur.');
      return;
    }

    setSubmittingEdit(true);
    try {
      // Endpoint güncellendi
      await api.put(`/istanbul-office/guests/${editingGuest.id}`, {
        visitor_name: editingGuest.visitor_name.trim(),
        host_name: editingGuest.host_name.trim(),
        reason: editingGuest.reason.trim(),
      });

      showCustomAlert('success', 'Misafir bilgileri güncellendi.');
      setIsEditModalOpen(false);
      fetchGuests();
    } catch (error) {
      console.error('Misafir güncelleme hatası:', error);
      showCustomAlert(
        'error',
        error.response?.data?.detail || 'Güncelleme başarısız.'
      );
    } finally {
      setSubmittingEdit(false);
    }
  };

  const requestExit = (id) => {
    setConfirmInfo({ show: true, id });
  };

  const confirmExit = async () => {
    if (!confirmInfo.id) return;

    setSubmittingExit(true);
    try {
      // Endpoint güncellendi
      await api.put(`/istanbul-office/guests/${confirmInfo.id}/exit`);
      showCustomAlert('success', 'Misafir çıkışı yapıldı.');
      fetchGuests();
    } catch (error) {
      console.error('Misafir çıkış hatası:', error);
      showCustomAlert(
        'error',
        error.response?.data?.detail || 'Çıkış işlemi başarısız.'
      );
    } finally {
      setSubmittingExit(false);
      setConfirmInfo({ show: false, id: null });
    }
  };

  const formatEntryTime = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box
      style={{
        padding: '24px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        style={{
          position: 'absolute',
          top: '-140px',
          right: '-120px',
          width: '340px',
          height: '340px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.08)',
          filter: 'blur(2px)',
        }}
      />
      <Box
        style={{
          position: 'absolute',
          bottom: '-180px',
          left: '-120px',
          width: '380px',
          height: '380px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.06)',
          filter: 'blur(2px)',
        }}
      />

      <Container size="xl" style={{ position: 'relative', zIndex: 2 }}>
        <Paper
          radius="xl"
          p="lg"
          mb="xl"
          bg="rgba(255,255,255,0.10)"
          style={{
            border: '1px solid rgba(255,255,255,0.18)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 20px 45px rgba(6, 25, 45, 0.20)',
          }}
        >
          <Group justify="space-between" align="center">
            <Group gap="md">
              <ThemeIcon
                size={64}
                radius="xl"
                variant="gradient"
                gradient={{ from: '#dbeafe', to: '#ffffff', deg: 135 }}
                style={{ boxShadow: '0 12px 28px rgba(3, 24, 46, 0.28)' }}
              >
                <IconUsers size={34} stroke={1.8} color="#0b5d9e" />
              </ThemeIcon>

              <div>
                <Badge radius="sm" size="md" variant="light" color="blue" mb={6}>
                  İstanbul Ofis
                </Badge>
                <Title order={2} c="black" fw={900}>
                  Misafir Takip Sistemi
                </Title>
                <Text c="blue.7" size="sm">
                  Yeni ziyaretçi kaydı oluşturun ve içerideki misafirleri yönetin.
                </Text>
              </div>
            </Group>

            <Group>
              <Button
                variant="light"
                color="gray"
                leftSection={<IconArrowLeft size={16} />}
                onClick={() => navigate('/istanbul-office/dashboard')}
              >
                Panele Dön
              </Button>

              <Button
                variant="gradient"
                gradient={{ from: '#0b5d9e', to: '#1c7ed6', deg: 120 }}
                leftSection={<IconRefresh size={16} />}
                onClick={fetchGuests}
              >
                Yenile
              </Button>
            </Group>
          </Group>
        </Paper>

        {alertInfo.show && (
          <Notification
            mb="lg"
            radius="md"
            withBorder
            color={alertInfo.type === 'success' ? 'teal' : 'red'}
            icon={
              alertInfo.type === 'success' ? (
                <IconCheck size="1.1rem" />
              ) : (
                <IconX size="1.1rem" />
              )
            }
            onClose={() => setAlertInfo({ show: false, type: '', message: '' })}
          >
            {alertInfo.message}
          </Notification>
        )}

        <SimpleGrid cols={{ base: 1, lg: 3 }} spacing="lg">
          <Paper
            p="xl"
            radius="xl"
            bg="rgba(255,255,255,0.96)"
            style={{
              border: '1px solid rgba(255,255,255,0.55)',
              boxShadow: '0 24px 55px rgba(6, 25, 45, 0.18)',
              backdropFilter: 'blur(8px)',
            }}
          >
            <Group mb="md">
              <ThemeIcon size={50} radius="xl" color="teal" variant="light">
                <IconUserPlus size={24} />
              </ThemeIcon>
              <div>
                <Text fw={700} size="lg">
                  Yeni Ziyaretçi Kaydı
                </Text>
                <Text size="sm" c="dimmed">
                  Misafir giriş bilgilerini doldurun.
                </Text>
              </div>
            </Group>

            <form onSubmit={handleEntry}>
              <Stack gap="md">
                
                {/* Radio Butonları Buraya Eklendi */}
                <Radio.Group
                  value={entryMode}
                  onChange={setEntryMode}
                  name="entryMode"
                >
                  <Group mt="xs" mb="xs">
                    <Radio value="name" label="İsim Girişi" color="teal" />
                    <Radio value="vehicle" label="Araç Girişi" color="teal" />
                  </Group>
                </Radio.Group>

                <TextInput
                  // Radio seçimine göre Label ve Placeholder dinamik değişiyor
                  label={entryMode === 'name' ? 'Misafir Adı Soyadı' : 'Araç Plaka'}
                  placeholder={entryMode === 'name' ? 'Örn: Ayşe Yılmaz' : 'Örn: 34 ABC 123'}
                  value={formData.visitor_name}
                  onChange={(e) =>
                    setFormData({ ...formData, visitor_name: e.currentTarget.value })
                  }
                  required
                  radius="md"
                />

                <TextInput
                  label="Kimi Ziyaret Edecek?"
                  placeholder="Örn: İnsan Kaynakları"
                  value={formData.host_name}
                  onChange={(e) =>
                    setFormData({ ...formData, host_name: e.currentTarget.value })
                  }
                  required
                  radius="md"
                />

                <TextInput
                  label="Sebep / Firma Adı"
                  placeholder="Örn: İş Görüşmesi"
                  value={formData.reason}
                  onChange={(e) =>
                    setFormData({ ...formData, reason: e.currentTarget.value })
                  }
                  radius="md"
                />

                <Button
                  type="submit"
                  fullWidth
                  mt="sm"
                  size="md"
                  radius="md"
                  loading={submittingEntry}
                  variant="gradient"
                  gradient={{ from: 'teal', to: 'cyan', deg: 120 }}
                  leftSection={<IconUserPlus size={18} />}
                >
                  Giriş Yap
                </Button>
              </Stack>
            </form>
          </Paper>

          <Paper
            p="xl"
            radius="xl"
            bg="rgba(255,255,255,0.96)"
            style={{
              border: '1px solid rgba(255,255,255,0.55)',
              boxShadow: '0 24px 55px rgba(6, 25, 45, 0.18)',
              backdropFilter: 'blur(8px)',
            }}
            sx={{ gridColumn: 'span 2' }}
          >
            <Group justify="space-between" mb="lg">
              <div>
                <Text fw={700} size="lg">
                  İçerideki Misafirler
                </Text>
                <Text size="sm" c="dimmed">
                  Aktif misafir sayısı: {activeGuests.length}
                </Text>
              </div>

              <Badge size="lg" radius="sm" color="blue" variant="light">
                Toplam: {activeGuests.length}
              </Badge>
            </Group>

            {loading ? (
              <Center py={80}>
                <Stack align="center" gap="xs">
                  <Loader color="blue" />
                  <Text size="sm" c="dimmed">
                    Misafir listesi yükleniyor...
                  </Text>
                </Stack>
              </Center>
            ) : activeGuests.length === 0 ? (
              <Center py={80}>
                <Stack align="center" gap="xs">
                  <ThemeIcon size={56} radius="xl" color="gray" variant="light">
                    <IconUsers size={26} />
                  </ThemeIcon>
                  <Text fw={600}>Şu an içeride misafir yok.</Text>
                  <Text size="sm" c="dimmed">
                    Yeni kayıtlar burada listelenecek.
                  </Text>
                </Stack>
              </Center>
            ) : (
              <Stack gap="md">
                {activeGuests.map((guest) => (
                  <Card
                    key={guest.id}
                    radius="xl"
                    withBorder
                    p="lg"
                    shadow="sm"
                    style={{
                      background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
                    }}
                  >
                    <Group justify="space-between" align="flex-start" wrap="wrap">
                      <Stack gap={6}>
                        <Group gap="sm">
                          <Text fw={700} size="md">
                            {guest.visitor_name}
                          </Text>
                          <Badge color="indigo" variant="light">
                            Aktif
                          </Badge>
                        </Group>

                        <Text size="sm" c="dimmed">
                          <Text span fw={600} c="dark">
                            Ziyaret Edilen:
                          </Text>{' '}
                          {guest.host_name}
                        </Text>

                        {guest.reason && (
                          <Text size="sm" c="dimmed">
                            <Text span fw={600} c="dark">
                              Sebep / Firma:
                            </Text>{' '}
                            {guest.reason}
                          </Text>
                        )}

                        <Text size="sm" c="dimmed">
                          <Text span fw={600} c="dark">
                            Giriş Saati:
                          </Text>{' '}
                          {formatEntryTime(guest.entry_time)}
                        </Text>
                      </Stack>

                      <Group gap="sm">
                        <Button
                          variant="light"
                          color="yellow"
                          leftSection={<IconEdit size={16} />}
                          onClick={() => openEditModal(guest)}
                        >
                          Düzenle
                        </Button>

                        <Button
                          color="red"
                          leftSection={<IconDoorExit size={16} />}
                          onClick={() => requestExit(guest.id)}
                        >
                          Çıkış
                        </Button>
                      </Group>
                    </Group>
                  </Card>
                ))}
              </Stack>
            )}
          </Paper>
        </SimpleGrid>
      </Container>

      <Modal
        opened={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Misafir Bilgilerini Düzenle"
        centered
        radius="xl"
        size="lg"
      >
        <form onSubmit={handleUpdateGuest}>
          <Stack gap="md">
            <TextInput
              label="Misafir Adı"
              value={editingGuest.visitor_name}
              onChange={(e) =>
                setEditingGuest({
                  ...editingGuest,
                  visitor_name: e.currentTarget.value,
                })
              }
              required
              radius="md"
            />

            <TextInput
              label="Ev Sahibi"
              value={editingGuest.host_name}
              onChange={(e) =>
                setEditingGuest({
                  ...editingGuest,
                  host_name: e.currentTarget.value,
                })
              }
              required
              radius="md"
            />

            <TextInput
              label="Sebep"
              value={editingGuest.reason}
              onChange={(e) =>
                setEditingGuest({
                  ...editingGuest,
                  reason: e.currentTarget.value,
                })
              }
              radius="md"
            />

            <Group justify="flex-end" mt="sm">
              <Button
                variant="default"
                onClick={() => setIsEditModalOpen(false)}
              >
                İptal
              </Button>

              <Button
                type="submit"
                loading={submittingEdit}
                variant="gradient"
                gradient={{ from: '#0b5d9e', to: '#1c7ed6', deg: 120 }}
              >
                Güncelle
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      <Modal
        opened={confirmInfo.show}
        onClose={() => setConfirmInfo({ show: false, id: null })}
        centered
        radius="xl"
        size="md"
        withCloseButton={false}
      >
        <Center mb="md">
          <ThemeIcon size={68} radius="xl" color="red" variant="light">
            <IconAlertTriangle size={32} />
          </ThemeIcon>
        </Center>

        <Title order={4} ta="center" mb="xs">
          Çıkış Onayı
        </Title>
        <Text ta="center" c="dimmed" mb="lg">
          Misafirin çıkış yaptığını onaylıyor musunuz?
        </Text>

        <Group justify="center">
          <Button
            variant="default"
            onClick={() => setConfirmInfo({ show: false, id: null })}
          >
            Vazgeç
          </Button>

          <Button
            color="red"
            loading={submittingExit}
            onClick={confirmExit}
          >
            Evet, Onaylıyorum
          </Button>
        </Group>
      </Modal>
    </Box>
  );
};

export default GuestTracking;